import { useCallback } from "react"
import { useMutation } from "urql"

import type {
  AddLocationToAssociationInput,
  AddLocationToAssociationMutation,
  AddLocationToAssociationMutationVariables,
} from "@/graphql/codegen/graphql"
import { AddLocationToAssociationDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseAddAssociationLocationReturn {
  addLocation: (input: AddLocationToAssociationInput) => Promise<boolean>
  isAdding: boolean
}

export const useAddAssociationLocation = (): UseAddAssociationLocationReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const [{ fetching }, executeMutation] = useMutation<
    AddLocationToAssociationMutation,
    AddLocationToAssociationMutationVariables
  >(AddLocationToAssociationDocument)

  const addLocation = useCallback(
    async (input: AddLocationToAssociationInput): Promise<boolean> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.companyProductAssociation.addLocation

      const hasError = error || output?.__typename === "AddLocationToAssociationFailure"

      if (hasError) {
        const message = output?.__typename === "AddLocationToAssociationFailure" ? output.error.message : error?.message
        notify({
          type: NotificationType.Error,
          silent: false,
          message: message ?? "Failed to associate location. Please try again.",
        })
        return false
      }

      notify({
        type: NotificationType.Success,
        silent: false,
        message: "Location associated",
      })
      return true
    },
    [executeMutation, notify]
  )

  return { addLocation, isAdding: fetching }
}
